import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Notification {
  id: string;
  severity: 'success' | 'info' | 'warn' | 'error';
  summary: string;
  detail: string;
  timestamp: Date;
  read: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications: Notification[] = [];
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);

  notifications$: Observable<Notification[]> = this.notificationsSubject.asObservable();
  unreadCount$: Observable<number> = this.unreadCountSubject.asObservable();

  constructor() {
    // Charger les notifications depuis localStorage au démarrage
    this.loadNotifications();
  }

  /**
   * Ajoute une nouvelle notification
   */
  addNotification(severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string): void {
    const notification: Notification = {
      id: this.generateId(),
      severity,
      summary,
      detail,
      timestamp: new Date(),
      read: false
    };

    this.notifications.unshift(notification);
    
    // Garder seulement les 50 dernières notifications
    if (this.notifications.length > 50) {
      this.notifications = this.notifications.slice(0, 50);
    }

    this.updateSubjects();
    this.saveNotifications();
  }

  /**
   * Marquer une notification comme lue
   */
  markAsRead(id: string): void {
    const notification = this.notifications.find(n => n.id === id);
    if (notification && !notification.read) {
      notification.read = true;
      this.updateSubjects();
      this.saveNotifications();
    }
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  markAllAsRead(): void {
    this.notifications.forEach(n => n.read = true);
    this.updateSubjects();
    this.saveNotifications();
  }

  /**
   * Supprimer une notification
   */
  deleteNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.updateSubjects();
    this.saveNotifications();
  }

  /**
   * Supprimer toutes les notifications
   */
  clearAll(): void {
    this.notifications = [];
    this.updateSubjects();
    this.saveNotifications();
  }

  /**
   * Obtenir toutes les notifications
   */
  getNotifications(): Notification[] {
    return [...this.notifications];
  }

  /**
   * Obtenir le nombre de notifications non lues
   */
  getUnreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  /**
   * Mettre à jour les subjects
   */
  private updateSubjects(): void {
    this.notificationsSubject.next([...this.notifications]);
    this.unreadCountSubject.next(this.getUnreadCount());
  }

  /**
   * Sauvegarder les notifications dans localStorage
   */
  private saveNotifications(): void {
    try {
      localStorage.setItem('brokinss_notifications', JSON.stringify(this.notifications));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des notifications:', error);
    }
  }

  /**
   * Charger les notifications depuis localStorage
   */
  private loadNotifications(): void {
    try {
      const stored = localStorage.getItem('brokinss_notifications');
      if (stored) {
        this.notifications = JSON.parse(stored).map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
        this.updateSubjects();
      }
    } catch (error) {
      console.error('Erreur lors du chargement des notifications:', error);
      this.notifications = [];
    }
  }

  /**
   * Générer un ID unique
   */
  private generateId(): string {
    return `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
